/**
 * «Список доступов» — все гостевые доступы тенанта (Phase 3, хаб «Гостевой
 * доступ»). ОТДЕЛЬНЫЙ ЭКРАН (правило feedback_every_form_is_a_separate_screen):
 * серверная пагинация GET /v1/guest-access?status=&page=&page_size= + фильтр-
 * чипы по статусу. Отмена доступа (owner/manager/front_desk) → бэк переводит
 * грант в «Просрочен» (expired) и рубит сессию гостя.
 *
 * Кнопка «Назад» снизу возвращает в хаб. RBAC гейтится на бэке (revoke —
 * owner/manager/front_desk).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Skeleton } from '@hsc/ui';
import type { GuestAccessBucket, GuestAccessGrant, GuestAccessStatus } from '@hsc/types';
import { useApproveGuestAccess, useCancelGuestAccess, useGuestAccessListPage } from './useGuestAccess.js';

const PAGE_SIZE = 20;

const FILTERS: { key: GuestAccessBucket; label: string }[] = [
  { key: '', label: 'Все' },
  { key: 'pending', label: 'Ожидает' },
  { key: 'active', label: 'Выдан' },
  { key: 'expired', label: 'Просрочен' },
];

function ruDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

function ruDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Чип статуса: метка + тон fleet-badge. */
function statusBadge(status: GuestAccessStatus): { label: string; tone: string } {
  switch (status) {
    case 'active':
      return { label: 'Выдан', tone: 'ok' };
    case 'pending_approval':
      return { label: 'Ожидает', tone: 'busy' };
    case 'expired':
    case 'revoked':
      return { label: 'Просрочен', tone: 'muted' };
    default:
      return { label: status, tone: 'muted' };
  }
}

export interface AccessListPageProps {
  /** Куда возвращает «Назад» (по умолчанию — хаб «Гостевой доступ»). */
  backTo?: string;
}

export function AccessListPage({ backTo = '/guest-access' }: AccessListPageProps): JSX.Element {
  const navigate = useNavigate();
  const [bucket, setBucket] = useState<GuestAccessBucket>('');
  const [page, setPage] = useState(1);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const listQ = useGuestAccessListPage({ status: bucket, page, page_size: PAGE_SIZE });
  const cancel = useCancelGuestAccess();
  const approve = useApproveGuestAccess();

  const doApprove = (id: string): void => {
    approve.mutate({ id, input: {} }, { onSuccess: () => void listQ.refetch() });
  };

  const grants = useMemo(() => listQ.data?.grants ?? [], [listQ.data]);
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeTo = Math.min(page * PAGE_SIZE, total);

  const changeBucket = (next: GuestAccessBucket): void => {
    setBucket(next);
    setPage(1);
  };

  const doCancel = (id: string): void => {
    cancel.mutate(id, {
      onSuccess: () => setConfirmId(null),
    });
  };

  return (
    <section data-testid="access-list-page" className="ga-page stays-page">
      <header className="staff-topbar">
        <div>
          <button
            type="button"
            className="ta-back-btn"
            onClick={() => navigate(backTo)}
            data-testid="access-list-back"
          >
            <span className="ta-back-btn__chev" aria-hidden="true">
              ‹
            </span>{' '}
            Назад
          </button>
          <h1 className="hsc-page__title staff-topbar__title">
            Список доступов{!listQ.isLoading && !listQ.isError ? ` · ${total}` : ''}
          </h1>
        </div>
      </header>

      <div className="fleet-statuses" role="tablist" data-testid="access-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key || 'all'}
            type="button"
            role="tab"
            aria-selected={bucket === f.key}
            className={`fleet-chip${bucket === f.key ? ' is-active' : ''}`}
            onClick={() => changeBucket(f.key)}
            data-testid={`access-filter-${f.key || 'all'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {listQ.isLoading ? (
        <div className="fleet-list" data-testid="access-skeleton">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} height={72} radius={14} />
          ))}
        </div>
      ) : listQ.isError ? (
        <Card>
          <p role="alert">Не удалось загрузить список доступов. Обновите страницу.</p>
        </Card>
      ) : grants.length === 0 ? (
        <Card>
          <p data-testid="access-empty" className="stays-empty">
            {bucket === ''
              ? 'Здесь пока пусто. Когда вы выдадите гостю доступ, он появится в этом списке.'
              : 'По этому фильтру доступов нет. Попробуйте другой статус.'}
          </p>
        </Card>
      ) : (
        <>
          <ul className="ga-pending-list" data-testid="access-list" aria-busy={listQ.isFetching}>
            {grants.map((g) => (
              <li key={g.id}>
                <AccessRow
                  grant={g}
                  confirming={confirmId === g.id}
                  busy={cancel.isPending}
                  approving={approve.isPending}
                  onApprove={() => doApprove(g.id)}
                  onAskCancel={() => setConfirmId(g.id)}
                  onConfirmCancel={() => doCancel(g.id)}
                  onDismiss={() => setConfirmId(null)}
                />
              </li>
            ))}
          </ul>

          {cancel.isError && (
            <p role="alert" className="staff-invite__msg staff-invite__msg--err" data-testid="access-cancel-error">
              Не удалось отменить доступ. Попробуйте ещё раз.
            </p>
          )}

          <div className="stays-pagination" data-testid="access-pagination">
            <span className="stays-pagination__info" data-testid="access-range">
              {rangeFrom}–{rangeTo} из {total}
            </span>
            <div className="stays-pagination__controls">
              <button
                type="button"
                className="stays-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || listQ.isFetching}
                data-testid="access-prev"
                aria-label="Предыдущая страница"
              >
                ←
              </button>
              <span className="stays-page-current" data-testid="access-page-current">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="stays-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || listQ.isFetching}
                data-testid="access-next"
                aria-label="Следующая страница"
              >
                →
              </button>
            </div>
          </div>
        </>
      )}

      <div className="staff-modal__actions ga-actions">
        <button
          type="button"
          className="staff-btn staff-btn--ghost"
          onClick={() => navigate(backTo)}
          data-testid="access-back"
        >
          Назад
        </button>
      </div>
    </section>
  );
}

/* ---------- Строка доступа + инлайн-confirm отмены ---------- */

function AccessRow({
  grant,
  confirming,
  busy,
  approving,
  onApprove,
  onAskCancel,
  onConfirmCancel,
  onDismiss,
}: {
  grant: GuestAccessGrant;
  confirming: boolean;
  busy: boolean;
  approving: boolean;
  onApprove: () => void;
  onAskCancel: () => void;
  onConfirmCancel: () => void;
  onDismiss: () => void;
}): JSX.Element {
  const badge = statusBadge(grant.status);
  const cancellable = grant.status === 'active' || grant.status === 'pending_approval';
  const approvable = grant.status === 'pending_approval';
  return (
    <div className="ga-pending-card" data-testid={`access-card-${grant.id}`}>
      <div className="ga-pending-card__top">
        <strong className="ga-pending-card__room">
          {grant.surname || 'Без фамилии'} · № {grant.room_number || '—'}
        </strong>
        <span className={`fleet-badge fleet-badge--${badge.tone}`} data-testid={`access-status-${grant.id}`}>
          {badge.label}
        </span>
      </div>
      <div className="ga-pending-card__dates">
        {ruDate(grant.check_in)} → {ruDate(grant.check_out)}
      </div>
      <div className="ga-pending-card__meta">
        Создан {ruDateTime(grant.created_at)}
        {grant.updated_at && grant.updated_at !== grant.created_at
          ? ` · обновлён ${ruDateTime(grant.updated_at)}`
          : ''}
      </div>
      {cancellable && (
        <div className="ga-actions ga-actions--row">
          {confirming ? (
            <>
              <span className="staff-hint" data-testid={`access-confirm-${grant.id}`}>
                Отменить доступ?
              </span>
              <button
                type="button"
                className="staff-btn staff-btn--ghost"
                onClick={onDismiss}
                disabled={busy}
                data-testid={`access-cancel-dismiss-${grant.id}`}
              >
                Нет
              </button>
              <button
                type="button"
                className="staff-btn staff-btn--danger"
                onClick={onConfirmCancel}
                disabled={busy}
                data-testid={`access-cancel-confirm-${grant.id}`}
              >
                {busy ? 'Отменяем…' : 'Да, отменить'}
              </button>
            </>
          ) : (
            <>
              {approvable && (
                <button
                  type="button"
                  className="staff-btn"
                  onClick={onApprove}
                  disabled={approving}
                  data-testid={`access-approve-${grant.id}`}
                >
                  {approving ? 'Принимаем…' : 'Принять'}
                </button>
              )}
              <button
                type="button"
                className="staff-btn staff-btn--danger"
                onClick={onAskCancel}
                data-testid={`access-cancel-${grant.id}`}
              >
                Отменить доступ
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
